import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  useTheme,
  alpha
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../core/hooks/useStore";
import { RootState } from "../../../../core/store/store";
import { KeywordJobData } from "../keyword-pig.slice";

const KeywordPigResultsTable = (props: { hideButtons?: boolean }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { apiUrl } = useSelector((state: RootState) => state.home);
  const toolData = useSelector((state: RootState) => state.keywordPig);
  const [searchTerm, setSearchTerm] = useState("");

  const results = toolData.tool.data
    ? (toolData.tool.data as KeywordJobData).data.keywords
    : null;

  if (!results) {
    return <></>;
  }

  // Filter results based on search term
  const filteredResults = results.filter(keyword => 
    keyword.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      {/* Search Bar */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search keywords..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          sx: {
            borderRadius: 2,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(theme.palette.primary.main, 0.2),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            }
          }
        }}
      />

      {/* Table */}
      <TableContainer 
        component={Paper} 
        elevation={0}
        sx={{ 
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          overflow: 'hidden'
        }}
      >
        <Table aria-label="keyword results table">
          <TableHead>
            <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
              <TableCell sx={{ fontWeight: 600, width: '60px' }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Keyword</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredResults.map((keyword, index) => (
              <TableRow
                key={index}
                sx={{ 
                  '&:hover': { 
                    backgroundColor: alpha(theme.palette.primary.main, 0.02) 
                  },
                  '&:last-child td, &:last-child th': { border: 0 },
                  transition: 'background-color 0.2s ease'
                }}
              >
                <TableCell>
                  <Chip
                    label={index + 1}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      minWidth: '32px',
                      userSelect: 'none'
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {keyword}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Empty State */}
      {filteredResults.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            bgcolor: alpha(theme.palette.grey[100], 0.5),
            borderRadius: 2,
            border: `2px dashed ${theme.palette.grey[300]}`,
            mt: 3
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No keywords found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search term to find keywords
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default KeywordPigResultsTable;
